import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, Phone, Globe, Building2, Stethoscope,
  Star, ChevronRight, User, Percent, Calendar
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const PROVIDER_IMAGES = [
  "https://images.pexels.com/photos/3845810/pexels-photo-3845810.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop",
  "https://images.pexels.com/photos/4226219/pexels-photo-4226219.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop",
  "https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop",
  "https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop",
];

const TYPE_LABELS = {
  CLINIC: { en: "Clinic", ar: "عيادة" },
  HOSPITAL: { en: "Hospital", ar: "مستشفى" },
  PHARMACY: { en: "Pharmacy", ar: "صيدلية" },
  LAB: { en: "Laboratory", ar: "معمل" },
  RADIOLOGY_CENTER: { en: "Radiology Center", ar: "مركز أشعة" },
  OTHER: { en: "Other", ar: "أخرى" },
};

function discountedPrice(original, type, value) {
  if (!original) return null;
  if (type === "PERCENTAGE") return Math.round(original * (1 - value / 100));
  if (type === "FIXED") return Math.max(0, original - value);
  return original;
}

export default function PublicProviderPage() {
  const { id } = useParams();
  const { lang, isRTL } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`${API}/api/public/providers/${id}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="pt-16 flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <p className="text-muted-foreground">{lang === "ar" ? "مزود الخدمة غير موجود" : "Provider not found"}</p>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {lang === "ar" ? "رجوع" : "Go back"}
          </Button>
        </div>
      </div>
    );
  }

  const { provider, categories, locations, staff, deals } = data;
  const t = (en, ar) => lang === "ar" ? ar : en;
  const name = provider.corporate_name;
  const desc = t(provider.description_en, provider.description_ar);
  const typeLabel = TYPE_LABELS[provider.type]?.[lang] || provider.type;
  const coverImg = provider.cover_url || PROVIDER_IMAGES[id.charCodeAt(0) % PROVIDER_IMAGES.length];

  return (
    <div className="min-h-screen bg-background" data-testid="public-provider-page">
      <PublicHeader />

      <main className="pt-16">
        {/* Cover + Header */}
        <div className="relative">
          <div className="h-48 sm:h-64 w-full overflow-hidden bg-muted">
            <img src={coverImg} alt={name} className="w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
          </div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-16 relative z-10">
            <div className="flex items-end gap-4">
              <div className="h-24 w-24 rounded-2xl border-4 border-background bg-card shadow-lg flex items-center justify-center overflow-hidden shrink-0">
                {provider.logo_url
                  ? <img src={provider.logo_url} alt={name} className="h-full w-full object-contain p-2" />
                  : <Building2 className="h-10 w-10 text-muted-foreground" />
                }
              </div>
              <div className="pb-2">
                <span className="inline-block rounded-full bg-primary/10 text-primary text-xs font-medium px-2.5 py-0.5 mb-1">{typeLabel}</span>
                <h1 className="text-2xl font-bold text-foreground leading-tight">{name}</h1>
                {(provider.neighborhoods || []).length > 0 && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span>{(provider.neighborhoods || []).slice(0, 3).join(" · ")}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-8">
          {/* Left column: details */}
          <div className="lg:col-span-2 space-y-8">

            {/* About */}
            {desc && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-3">{t("About", "نبذة عن المزود")}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </section>
            )}

            {/* Specialties */}
            {categories.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-3">{t("Specialties", "التخصصات")}</h2>
                <div className="flex flex-wrap gap-2">
                  {categories.map(c => (
                    <span key={c.id} className="rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-foreground">
                      {t(c.name_en, c.name_ar)}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Active Deals */}
            {deals.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">{t("Active Deals", "العروض الحالية")}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {deals.map(deal => {
                    const discounted = discountedPrice(deal.original_price, deal.discount_type, deal.discount_value);
                    return (
                      <Link key={deal.id} to={`/deal/${deal.id}`}
                        className="surface-card p-4 hover:shadow-md transition-shadow group block">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-bold px-2 py-0.5">
                            <Percent className="h-3 w-3" />
                            {deal.discount_value}{deal.discount_type === "PERCENTAGE" ? "%" : " EGP"} {t("OFF", "خصم")}
                          </div>
                          {deal.end_date && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              {t("Until", "حتى")} {deal.end_date?.slice(5)}
                            </div>
                          )}
                        </div>
                        <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors leading-snug mb-1">
                          {t(deal.title_en, deal.title_ar)}
                        </h3>
                        {deal.original_price && (
                          <div className="flex items-baseline gap-2 mt-2">
                            <span className="text-base font-bold text-foreground">{discounted?.toLocaleString()} EGP</span>
                            <span className="text-xs text-muted-foreground line-through">{deal.original_price?.toLocaleString()}</span>
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Our Experts */}
            {staff.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">{t("Our Experts", "أطباؤنا")}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {staff.map(s => (
                    <div key={s.id} className="surface-card p-4 flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-muted overflow-hidden shrink-0">
                        {s.photo_url
                          ? <img src={s.photo_url} alt={t(s.full_name_en, s.full_name_ar)} className="h-full w-full object-cover" />
                          : <div className="h-full w-full flex items-center justify-center"><User className="h-5 w-5 text-muted-foreground" /></div>
                        }
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-foreground">{t(s.full_name_en, s.full_name_ar)}</div>
                        {s.title_en && <div className="text-xs text-muted-foreground">{t(s.title_en, s.title_ar)}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right column: sidebar */}
          <div className="space-y-5">

            {/* CTA card */}
            {!user && (
              <div className="surface-card p-5 text-center">
                <Star className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-1">{t("Unlock Member Deals", "افتح عروض العضوية")}</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  {t("Join Hemaia to access exclusive discounts at this provider.", "انضم لحماية للحصول على خصومات حصرية.")}
                </p>
                <Button className="w-full rounded-xl" size="sm" onClick={() => navigate("/signup")}>
                  {t("Join Now", "انضم الآن")}
                </Button>
                <Button variant="ghost" size="sm" className="w-full mt-1 text-xs" onClick={() => navigate("/login")}>
                  {t("Already a member? Sign in", "عضو بالفعل؟ سجل دخولك")}
                </Button>
              </div>
            )}

            {/* Contact */}
            {(provider.phone_1 || provider.website) && (
              <div className="surface-card p-4 space-y-3">
                <h3 className="font-semibold text-sm text-foreground">{t("Contact", "تواصل")}</h3>
                {provider.phone_1 && (
                  <a href={`tel:${provider.phone_1}`} className="flex items-center gap-3 text-sm text-foreground hover:text-primary transition-colors">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    {provider.phone_1}
                  </a>
                )}
                {provider.website && (
                  <a href={provider.website} target="_blank" rel="noreferrer"
                    className="flex items-center gap-3 text-sm text-foreground hover:text-primary transition-colors">
                    <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                    {t("Visit Website", "زيارة الموقع")}
                  </a>
                )}
              </div>
            )}

            {/* Locations */}
            {locations.length > 0 && (
              <div className="surface-card p-4 space-y-3">
                <h3 className="font-semibold text-sm text-foreground">{t("Branches", "الفروع")}</h3>
                {locations.map(loc => (
                  <div key={loc.id} className="flex gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-foreground">{t(loc.name_en, loc.name_ar)}</div>
                      {loc.address_en && <div className="text-xs text-muted-foreground">{t(loc.address_en, loc.address_ar)}</div>}
                      {loc.neighborhood && <div className="text-xs text-muted-foreground">{loc.neighborhood}</div>}
                      {loc.phone && (
                        <a href={`tel:${loc.phone}`} className="text-xs text-primary hover:underline">{loc.phone}</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
